import axios from 'axios';
import React, { ReactElement, useEffect, useState } from 'react';

import AppContext from './context';
import {
  AppValue,
  OrganizationBrandData,
  PlacementType,
  ReportDataType,
} from './types';

interface AppProviderProps {
  children?: ReactElement;
  radius?: number;
  zipCode?: string;
  primaryBrand?: OrganizationBrandData;
  secondaryBrand?: OrganizationBrandData;
  tertiaryBrand?: OrganizationBrandData;
}

const AppProvider: React.FC<AppProviderProps> = (props) => {
  const DEBUG = false;

  const [appTitle, setAppTitle] = useState<string>('radian™');
  const [zipCode, setZipCode] = useState<string>('');
  const [radius, setRadius] = useState<number>(10);
  const [riskDriverLimit, setRiskDriverLimit] = useState<number>(3);
  const [geoId, setGeoId] = useState<number>(0);
  const [geoIds, setGeoIds] = useState<number[]>([]);
  const [userId, updateUserId] = useState<number>();
  const [organizationName, setOrganizationName] = useState<string>('');
  const _primaryBrand: OrganizationBrandData =
    (DEBUG)
      ? {
        id: 131,
        name: 'Northwell Health',
        placement: 'primary',
        colors: {
            light: 'hsl(207, 100%, 95%)',
            primary: 'hsl(207, 95%, 33%)',
            dark: 'hsl(207, 100%, 10%)'
        },
        logoUrl: 'https://lb-jcl.s3.us-west-2.amazonaws.com/logos/1665675891002-1200px-Northwell_Health_logo.svg.png'
      }
      : {
        id: -1,
        name: organizationName,
        placement: 'primary',
        colors: {
          light: 'rgb(246, 250, 252)',
          primary: 'rgb(10, 115, 186)',
          dark: 'rgb(5, 69, 111)',
        }
      }
    ;
  const [primaryBrand, setPrimaryBrand] = useState<OrganizationBrandData>(_primaryBrand);
  const [secondaryBrand, setSecondaryBrand] = useState<OrganizationBrandData>({
    id: -1,
    name: '',
    placement: 'secondary',
  });
  const [tertiaryBrand, setTertiaryBrand] = useState<OrganizationBrandData>({
    id: -1,
    name: '',
    placement: 'tertiary',
  });

  const _reportData: ReportDataType = 
    (DEBUG)
    ? 
      {
        uuid: '8c81529d-e3f8-48b7-90c7-c23f8e44c550',
        id: 176,
        userId: -1,
        updatedAt: '2022-10-13T16:15:23.360Z',
        createdAt: '2022-10-13T16:15:23.360Z',
      }
    : 
      null;
  const [reportData, setReportData] = useState<ReportDataType>(_reportData);

  const setOrganizationBrand = (organizationBrand: OrganizationBrandData, placement: PlacementType) => {
    switch (placement) {
      case 'primary':
        setPrimaryBrand(organizationBrand);
        break;
      case 'secondary':
        setSecondaryBrand(organizationBrand);
        break;
      case 'tertiary':
        setTertiaryBrand(organizationBrand);
        break;
      default:
        console.warn('Incorrect placement specified: `%s`. Please use `primary`, `secondary`, or `tertiary.`');
        return;
    }
  };

  const setUserId = (userId: number) => {
    const storage = window.localStorage;
    storage.setItem('userId', `${userId}`);
    if (reportData && reportData.id > -1) {
      axios.post('/update_report/', {
        reportId: reportData.id,
        key: 'userId',
        value: userId,
      });
    }
    updateUserId(userId);
  };

  useEffect(() => {
    const userId = localStorage.getItem('userId') || -1;
    updateUserId(parseInt(`${userId}`));
  }, []);

  useEffect(() => {
    const newName = { name: organizationName };
    const _primaryBrand = Object.assign(primaryBrand, newName);
    setPrimaryBrand(_primaryBrand);
  },[organizationName]);

  useEffect(() => {
    document.title = appTitle;
  }, [appTitle]);

  const value: AppValue = {
    appTitle,
    geoId,
    geoIds,
    radius,
    reportData,
    riskDriverLimit,
    userId,
    zipCode,
    primaryBrand,
    secondaryBrand,
    tertiaryBrand,
    setAppTitle,
    setGeoId,
    setGeoIds,
    setOrganizationBrand,
    setOrganizationName,
    setRadius,
    setReportData,
    setRiskDriverLimit,
    setUserId,
    setZipCode,
  };

  return <AppContext.Provider value={value} {...props} />;
};

export default AppProvider;
